import type {
  Answer,
  Question,
  GroupedQuestionField,
  GroupedQuestionnaireData,
  StandardQuestionnaireData,
} from '../types/api';
import {
  isStandardQuestionnaire,
  isGroupedQuestionnaire,
} from './questionnaireUtils';

/**
 * Check if a question has attachments uploaded
 */
const hasAttachments = (question: Question | GroupedQuestionField): boolean => {
  return (
    (Array.isArray(question.attachments) && question.attachments.length > 0) ||
    (Array.isArray(question.uploaded_filenames) &&
      question.uploaded_filenames.length > 0)
  );
};

/**
 * Check if the selected option requires attachment
 */
const doesSelectedOptionRequireAttachment = (
  question: Question | GroupedQuestionField,
  selectedOptionId: string | null,
  typedValue : string | null
): boolean => {
  console.log('Checking for answer validation::', question, selectedOptionId);
  //if there is no selected option but there is typed value, then check if question itself requires attachment
  if (!selectedOptionId && typedValue && typedValue?.length > 0) {
    return question.has_attachment || question.hasAttachment;
  }
  if (!selectedOptionId || !question.options) {
    return false;
  }
  const selectedOption = question.options.find(
    opt => String(opt.id) === String(selectedOptionId)
  );


  return !!(selectedOption && selectedOption.has_attachment);
};

/**
 * Check if a single question has missing required attachments
 */
const checkQuestionForMissingAttachments = (
  question: Question | GroupedQuestionField,
  answer: Answer | undefined,
  answers?: Record<string, Answer>
): boolean => {
  // Skip if question is excluded
  if (question.excluded || !question.hasAttachment) {
    return false;
  }

  // Special handling for radio-grid custom questions
  if (
    (question as any).type === 'custom' &&
    (question as any).is_radio_grid &&
    (question as any).subQuestions
  ) {
    // For radio-grid, check if ANY subquestion has selected an option requiring attachment
    let requiresAttachment = false;

    if (answers) {
      for (const sub of (question as any).subQuestions) {
        const subAnswer = answers[sub.id];
        if (subAnswer?.value?.selectedOptionId) {
          const selectedOptionId = subAnswer.value.selectedOptionId;
          const typedValue = subAnswer.value.typedValue;
          if (doesSelectedOptionRequireAttachment(sub, selectedOptionId, typedValue)) {
            requiresAttachment = true;
            break;
          }
        }
      }
    }

    // If any subquestion requires attachment, check main question's attachments
    if (requiresAttachment && !hasAttachments(question)) {
      return true;
    }

    return false;
  }

  // Skip if no answer provided
  if (!answer?.value) {
    return false;
  }

  const { selectedOptionId, typedValue } = answer.value;

  // Check if the selected option requires attachment
  const requiresAttachment = doesSelectedOptionRequireAttachment(
    question,
    selectedOptionId,
    typedValue
  );

  // If attachment is required but not uploaded, return true (missing)
  if (requiresAttachment && !hasAttachments(question)) {
    return true;
  }

  // Subquestions are handled at the top level of the answers object
  // They have their own IDs and don't need recursive checking here

  return false;
};

/**
 * Helper to collect all fields including subquestions
 */
const collectAllFields = (
  questionnaireData: GroupedQuestionnaireData | StandardQuestionnaireData
): Array<Question | GroupedQuestionField> => {
  const allFields: Array<Question | GroupedQuestionField> = [];

  if (isStandardQuestionnaire(questionnaireData)) {
    const fields = questionnaireData.metadata.fields;
    for (const field of fields) {
      allFields.push(field);
      // Add subquestions as separate fields
      if (field.subQuestions && field.subQuestions.length > 0) {
        field.subQuestions.forEach(subQ => allFields.push(subQ as Question));
      }
    }
  } else if (isGroupedQuestionnaire(questionnaireData)) {
    for (const group of questionnaireData.groups) {
      for (const field of group.metadata.fields) {
        allFields.push(field);
        // Add subquestions as separate fields
        if (field.subQuestions && field.subQuestions.length > 0) {
          field.subQuestions.forEach(subQ => allFields.push(subQ as any));
        }
      }
    }
  }

  return allFields;
};

/**
 * Check if there are any questions with missing required attachments
 * Returns true if there are missing attachments, false otherwise
 */
export const checkForMissingAttachments = (
  questionnaireData: GroupedQuestionnaireData | StandardQuestionnaireData,
  answers: Record<string, Answer>
): boolean => {
  console.log('🔍 Checking for missing attachments', {
    questionnaireData,
    answers,
  });

  let hasMissingAttachments = false;

  // Collect all fields including subquestions
  const allFields = collectAllFields(questionnaireData);

  // Check each field
  for (const field of allFields) {
    const answer = answers[field.id];
    if (checkQuestionForMissingAttachments(field, answer, answers)) {
      const fieldLabel =
        'label' in field
          ? field.label
          : (field as GroupedQuestionField).question;
      console.log('❌ Missing attachment for question:', field.id, fieldLabel);
      hasMissingAttachments = true;
    }
  }

  console.log('✅ Missing attachments check result:', hasMissingAttachments);
  return hasMissingAttachments;
};

/**
 * Get list of question labels that have missing required attachments
 */
export const getMissingAttachmentQuestions = (
  questionnaireData: GroupedQuestionnaireData | StandardQuestionnaireData,
  answers: Record<string, Answer>
): string[] => {
  const missingQuestions: string[] = [];

  // Collect all fields including subquestions
  const allFields = collectAllFields(questionnaireData);

  // Check each field
  for (const field of allFields) {
    if (field.excluded) continue;

    // Special handling for radio-grid custom questions
    if (
      (field as any).type === 'custom' &&
      (field as any).component === 'radio-grid' &&
      (field as any).subQuestions
    ) {
      let requiresAttachment = false;

      for (const sub of (field as any).subQuestions) {
        const subAnswer = answers[sub.id];
        if (subAnswer?.value?.selectedOptionId) {
          const selectedOptionId = subAnswer.value.selectedOptionId;
          if (doesSelectedOptionRequireAttachment(sub, selectedOptionId)) {
            requiresAttachment = true;
            break;
          }
        }
      }

      if (requiresAttachment && !hasAttachments(field)) {
        let label: string;
        if ('q_number' in field && field.q_number) {
          const text =
            'label' in field
              ? field.label
              : (field as GroupedQuestionField).question;
          label = `${field.q_number} ${text}`;
        } else {
          label =
            'label' in field
              ? field.label
              : (field as GroupedQuestionField).question;
        }
        missingQuestions.push(label);
      }
      continue;
    }

    const answer = answers[field.id];
    if (!answer?.value) continue;

    const { selectedOptionId } = answer.value;
    const requiresAttachment = doesSelectedOptionRequireAttachment(
      field,
      selectedOptionId
    );

    if (requiresAttachment && !hasAttachments(field)) {
      // Handle different field types
      let label: string;
      if ('q_number' in field && field.q_number) {
        const text =
          'label' in field
            ? field.label
            : (field as GroupedQuestionField).question;
        label = `${field.q_number} ${text}`;
      } else {
        label =
          'label' in field
            ? field.label
            : (field as GroupedQuestionField).question;
      }
      missingQuestions.push(label);
    }
  }

  return missingQuestions;
};

/**
 * Find the first question with missing required attachments
 * Returns the question ID and group key (for grouped questionnaires)
 */
export const findFirstMissingAttachmentQuestion = (
  questionnaireData: GroupedQuestionnaireData | StandardQuestionnaireData,
  answers: Record<string, Answer>
): { questionId: string; groupKey?: string; questionLabel: string } | null => {
  const getAnswerValue = (field: any) => {
    const ans = answers?.[field.id];
    if (ans?.value) return ans.value;
    return field.default_value;
  };

  const checkField = (field: any, groupKey?: string): any => {
    if (field.excluded) return null;

    // Special handling for radio-grid custom questions
    if (
      field.type === 'custom' &&
      field.component === 'radio-grid' &&
      field.subQuestions
    ) {
      console.log(
        '🔍 Checking radio-grid question:',
        field.id,
        field.label || field.question
      );

      // For radio-grid, check if ANY subquestion has selected an option requiring attachment
      let requiresAttachment = false;

      for (const sub of field.subQuestions) {
        const answerObj = getAnswerValue(sub);
        if (!answerObj) continue;
        const { selectedOptionId, typedValue } = answerObj.selectedOptionId;

        if (
          selectedOptionId &&
          doesSelectedOptionRequireAttachment(sub, selectedOptionId, typedValue)
        ) {
          console.log(
            '✓ Radio-grid subquestion requires attachment:',
            sub.id,
            sub.question
          );
          requiresAttachment = true;
          break;
        }
      }

      // If any subquestion requires attachment, check main question's attachments
      if (requiresAttachment && !hasAttachments(field)) {
        console.log('❌ Radio-grid question missing attachments:', field.id);
        const label =
          field.q_number && (field.label || field.question)
            ? ` ${field.label || field.question}`
            : field.label || field.question || '';

        return { questionId: field.id, groupKey, questionLabel: label };
      }

      console.log('✅ Radio-grid question has attachments or not required');
      return null;
    }

    // Skip other custom types
    if (field.type === 'custom') return null;

    // Check subquestions first (for non-radio-grid questions)
    if (Array.isArray(field.subQuestions) && field.subQuestions.length > 0) {
      for (const sub of field.subQuestions) {
        const res = checkField(sub, groupKey);
        if (res) return res;
      }
      return null;
    }

    const answerObj = getAnswerValue(field);
    if (!answerObj) return null;
    const { selectedOptionId, typedValue } = answerObj;

    //if both selectedOptionId and typedValue are null/empty, skip
    if (!selectedOptionId && !typedValue) return null;
    if (
      doesSelectedOptionRequireAttachment(field, selectedOptionId, typedValue) &&
      !hasAttachments(field)
    ) {
      const label =
        field.q_number && (field.label || field.question)
          ? `${field.label || field.question}`
          : field.label || field.question || '';

      return { questionId: field.id, groupKey, questionLabel: label };
    }

    return null;
  };

  if (isStandardQuestionnaire(questionnaireData)) {
    for (const field of collectAllFields(questionnaireData)) {
      const res = checkField(field);
      if (res) return res;
    }
  } else if (isGroupedQuestionnaire(questionnaireData)) {
    for (const group of questionnaireData.groups) {
      for (const field of group.metadata.fields) {
        const res = checkField(field, group.key);
        if (res) return res;
      }
    }
  }

  return null;
};
